/**
 * Service Worker Hook
 * Manages service worker registration, updates, and communication
 * Provides offline capabilities and caching for the trading platform
 */

'use client';

import { useEffect, useState, useCallback } from 'react';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface ServiceWorkerState {
  /** Whether service worker is supported */
  isSupported: boolean;
  /** Whether service worker is registered */
  isRegistered: boolean;
  /** Whether service worker is active */
  isActive: boolean;
  /** Whether there's an update available */
  hasUpdate: boolean;
  /** Whether service worker is installing */
  isInstalling: boolean;
  /** Service worker registration */
  registration: ServiceWorkerRegistration | null;
  /** Error message if registration failed */
  error: string | null;
}

export interface ServiceWorkerOptions {
  /** Service worker script path */
  scriptPath?: string;
  /** Whether to enable service worker */
  enabled?: boolean;
  /** Update check interval in milliseconds */
  updateCheckInterval?: number;
  /** Whether to show update notification */
  showUpdateNotification?: boolean;
}

// =============================================================================
// SERVICE WORKER HOOK
// =============================================================================

export function useServiceWorker(options: ServiceWorkerOptions = {}) {
  const {
    scriptPath = '/sw.js',
    enabled = true,
    updateCheckInterval = 60000, // 1 minute
    showUpdateNotification: shouldShowUpdateNotification = true,
  } = options;

  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: typeof window !== 'undefined' && 'serviceWorker' in navigator,
    isRegistered: false,
    isActive: false,
    hasUpdate: false,
    isInstalling: false,
    registration: null,
    error: null,
  });

  // Register service worker
  const register = useCallback(async () => {
    if (!state.isSupported || !enabled) {
      return;
    }

    try {
      console.log('Service Worker: Registering...');

      const registration = await navigator.serviceWorker.register(scriptPath, {
        scope: '/',
      });

      setState(prev => ({
        ...prev,
        isRegistered: true,
        registration,
        error: null,
      }));

      console.log('Service Worker: Registered successfully');

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        console.log('Service Worker: Update found');
        setState(prev => ({ ...prev, isInstalling: true }));

        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('Service Worker: Update ready');
              setState(prev => ({
                ...prev,
                hasUpdate: true,
                isInstalling: false
              }));

              if (shouldShowUpdateNotification) {
                showUpdateNotification();
              }
            }
          });
        }
      });

      // Listen for controller change
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Service Worker: Controller changed');
        setState(prev => ({
          ...prev,
          isActive: true,
          hasUpdate: false
        }));

        // Reload page to use new service worker
        window.location.reload();
      });

    } catch (error) {
      console.error('Service Worker: Registration failed:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Registration failed',
      }));
    }
  }, [state.isSupported, enabled, scriptPath, shouldShowUpdateNotification]);

  // Update service worker
  const update = useCallback(async () => {
    if (!state.registration) {
      return;
    }

    try {
      console.log('Service Worker: Updating...');
      await state.registration.update();
    } catch (error) {
      console.error('Service Worker: Update failed:', error);
    }
  }, [state.registration]);

  // Skip waiting and activate new service worker
  const skipWaiting = useCallback(async () => {
    if (!state.registration || !state.registration.waiting) {
      return;
    }

    try {
      console.log('Service Worker: Skipping waiting...');
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } catch (error) {
      console.error('Service Worker: Skip waiting failed:', error);
    }
  }, [state.registration]);

  // Unregister service worker
  const unregister = useCallback(async () => {
    if (!state.registration) {
      return;
    }

    try {
      console.log('Service Worker: Unregistering...');
      await state.registration.unregister();

      setState(prev => ({
        ...prev,
        isRegistered: false,
        isActive: false,
        hasUpdate: false,
        registration: null,
      }));

      console.log('Service Worker: Unregistered successfully');
    } catch (error) {
      console.error('Service Worker: Unregistration failed:', error);
    }
  }, [state.registration]);

  // Cache API data
  const cacheApiData = useCallback(async (data: Record<string, any>) => {
    if (!state.registration || !state.registration.active) {
      return;
    }

    try {
      state.registration.active.postMessage({
        type: 'CACHE_API_DATA',
        payload: data,
      });
    } catch (error) {
      console.error('Service Worker: Cache API data failed:', error);
    }
  }, [state.registration]);

  // Clear cache
  const clearCache = useCallback(async (cacheName?: string) => {
    if (!state.registration || !state.registration.active) {
      return;
    }

    try {
      state.registration.active.postMessage({
        type: 'CLEAR_CACHE',
        payload: cacheName,
      });
    } catch (error) {
      console.error('Service Worker: Clear cache failed:', error);
    }
  }, [state.registration]);

  // Show update notification
  const showUpdateNotification = useCallback(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification('Mechagora Trading', {
          body: 'A new version is available. Click to update.',
          icon: '/icon-192x192.png',
          badge: '/badge-72x72.png',
          tag: 'update-available',
        });
      } else if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            showUpdateNotification();
          }
        });
      }
    }
  }, []);

  // Register service worker on mount
  useEffect(() => {
    register();
  }, [register]);

  // Set up periodic update checks
  useEffect(() => {
    if (!state.isRegistered || !updateCheckInterval) {
      return;
    }

    const interval = setInterval(() => {
      update();
    }, updateCheckInterval);

    return () => clearInterval(interval);
  }, [state.isRegistered, updateCheckInterval, update]);

  // Listen for service worker messages
  useEffect(() => {
    if (!state.isSupported) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const { type, timestamp } = event.data;

      switch (type) {
        case 'BACKGROUND_SYNC_COMPLETE':
          console.log('Service Worker: Background sync completed at', new Date(timestamp));
          break;

        default:
          console.log('Service Worker: Received message:', type);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [state.isSupported]);

  return {
    ...state,
    register,
    update,
    skipWaiting,
    unregister,
    cacheApiData,
    clearCache,
  };
}

// =============================================================================
// SERVICE WORKER UTILITIES
// =============================================================================

export function isServiceWorkerSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator;
}

export function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isServiceWorkerSupported()) {
    return Promise.resolve(null);
  }

  return navigator.serviceWorker.getRegistration().then(reg => reg || null);
}

export function getServiceWorkerController(): ServiceWorker | null {
  if (!isServiceWorkerSupported()) {
    return null;
  }

  return navigator.serviceWorker.controller;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  return Notification.requestPermission();
}

export function showNotification(title: string, options?: NotificationOptions): void {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return;
  }

  if (Notification.permission === 'granted') {
    new Notification(title, options);
  }
}
